/**
 * Vehicle sub-module UI config. API method names must exist on MainService.
 * Grid POST body: { Page, PageSize, Keyword?, VehicleId? }
 */

export const VEHICLE_FUEL_LOGS = {
  title: 'Vehicle Fuel Logs',
  subtitle: 'Fuel entries by vehicle',
  gridKey: 'VehicleFuelLogsInGrid',
  saveKey: 'SaveVehicleFuelLog',
  deleteKey: 'DeleteVehicleFuelLog',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'FillDate', header: 'Date' },
    { key: 'Liters', header: 'Liters' },
    { key: 'Amount', header: 'Amount' },
    { key: 'OdometerReading', header: 'Odometer' },
    { key: 'StationName', header: 'Station' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'FillDate', label: 'Fill date', type: 'date', required: true },
    { name: 'Liters', label: 'Liters', type: 'number', step: '0.01' },
    { name: 'Amount', label: 'Amount', type: 'number', step: '0.01' },
    { name: 'OdometerReading', label: 'Odometer', type: 'number' },
    { name: 'StationName', label: 'Station', type: 'text' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

export const VEHICLE_PERMITS = {
  title: 'Vehicle Permits',
  subtitle: 'Permits and renewals',
  gridKey: 'VehiclePermitsInGrid',
  saveKey: 'SaveVehiclePermit',
  deleteKey: 'DeleteVehiclePermit',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'PermitType', header: 'Type' },
    { key: 'PermitNumber', header: 'Number' },
    { key: 'IssueDate', header: 'Issue' },
    { key: 'ExpiryDate', header: 'Expiry' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'PermitType', label: 'Permit type', type: 'text', required: true },
    { name: 'PermitNumber', label: 'Permit number', type: 'text' },
    { name: 'IssueDate', label: 'Issue date', type: 'date' },
    { name: 'ExpiryDate', label: 'Expiry date', type: 'date' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

export const VEHICLE_INSPECTIONS = {
  title: 'Vehicle Inspections',
  subtitle: 'Safety and periodic inspections',
  gridKey: 'VehicleInspectionsInGrid',
  saveKey: 'SaveVehicleInspection',
  deleteKey: 'DeleteVehicleInspection',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'InspectionDate', header: 'Date' },
    { key: 'Result', header: 'Result' },
    { key: 'InspectorName', header: 'Inspector' },
    { key: 'NextDueDate', header: 'Next due' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'InspectionDate', label: 'Inspection date', type: 'date', required: true },
    { name: 'Result', label: 'Result', type: 'text' },
    { name: 'InspectorName', label: 'Inspector', type: 'text' },
    { name: 'NextDueDate', label: 'Next due', type: 'date' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

export const VEHICLE_ODOMETER_LOGS = {
  title: 'Vehicle Odometer Logs',
  subtitle: 'Odometer readings over time',
  gridKey: 'VehicleOdometerLogsInGrid',
  saveKey: 'SaveVehicleOdometerLog',
  deleteKey: 'DeleteVehicleOdometerLog',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'ReadingDate', header: 'Date' },
    { key: 'Reading', header: 'Reading (km)' },
    { key: 'Source', header: 'Source' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'ReadingDate', label: 'Reading date', type: 'date', required: true },
    { name: 'Reading', label: 'Odometer (km)', type: 'number', required: true },
    { name: 'Source', label: 'Source', type: 'text' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

export const VEHICLE_ACCIDENTS = {
  title: 'Vehicle Accidents',
  subtitle: 'Incident records',
  gridKey: 'VehicleAccidentsInGrid',
  saveKey: 'SaveVehicleAccident',
  deleteKey: 'DeleteVehicleAccident',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'AccidentDate', header: 'Date' },
    { key: 'Description', header: 'Description' },
    { key: 'Location', header: 'Location' },
    { key: 'ClaimReference', header: 'Claim ref' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'AccidentDate', label: 'Date', type: 'date', required: true },
    { name: 'Description', label: 'Description', type: 'text' },
    { name: 'Location', label: 'Location', type: 'text' },
    { name: 'ClaimReference', label: 'Claim reference', type: 'text' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

export const VEHICLE_EXPENSES = {
  title: 'Vehicle Expenses',
  subtitle: 'Linked to expense master',
  gridKey: 'VehicleExpensesInGrid',
  saveKey: 'SaveVehicleExpense',
  deleteKey: 'DeleteVehicleExpense',
  expenseDropdown: true,
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'ExpenseName', header: 'Expense' },
    { key: 'ExpenseDate', header: 'Date' },
    { key: 'Amount', header: 'Amount' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'ExpenseDate', label: 'Date', type: 'date', required: true },
    { name: 'Amount', label: 'Amount', type: 'number', step: '0.01', required: true },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

export const VEHICLE_SERVICE_HISTORY = {
  title: 'Vehicle Service History',
  subtitle: 'Workshop visits and service notes',
  gridKey: 'VehicleServiceHistoryInGrid',
  saveKey: 'SaveVehicleServiceHistory',
  deleteKey: 'DeleteVehicleServiceHistory',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'ServiceDate', header: 'Date' },
    { key: 'ServiceType', header: 'Type' },
    { key: 'WorkshopName', header: 'Workshop' },
    { key: 'Cost', header: 'Cost' },
    { key: 'NextServiceDue', header: 'Next due' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'ServiceDate', label: 'Service date', type: 'date', required: true },
    { name: 'ServiceType', label: 'Service type', type: 'text' },
    { name: 'WorkshopName', label: 'Workshop', type: 'text' },
    { name: 'Cost', label: 'Cost', type: 'number', step: '0.01', nullable: true },
    { name: 'NextServiceDue', label: 'Next service due', type: 'date' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

/** Same API as service history; Maintenance menu uses this until a separate maintenance entity exists. */
export const VEHICLE_MAINTENANCE = {
  title: 'Vehicle maintenance',
  subtitle: 'Workshop visits and service records (same data as Service history)',
  gridKey: 'VehicleServiceHistoryInGrid',
  saveKey: 'SaveVehicleServiceHistory',
  deleteKey: 'DeleteVehicleServiceHistory',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'ServiceDate', header: 'Date' },
    { key: 'ServiceType', header: 'Type' },
    { key: 'WorkshopName', header: 'Workshop' },
    { key: 'Cost', header: 'Cost' },
    { key: 'NextServiceDue', header: 'Next due' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'ServiceDate', label: 'Service date', type: 'date', required: true },
    { name: 'ServiceType', label: 'Service type', type: 'text' },
    { name: 'WorkshopName', label: 'Workshop / provider', type: 'text' },
    { name: 'Cost', label: 'Cost', type: 'number', step: '0.01', nullable: true },
    { name: 'NextServiceDue', label: 'Next service due', type: 'date' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

export const VEHICLE_DOCUMENTS = {
  title: 'Vehicle documents',
  subtitle: 'Licences, certificates, and file path or URL',
  gridKey: 'VehicleDocumentsInGrid',
  saveKey: 'SaveVehicleDocument',
  deleteKey: 'DeleteVehicleDocument',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'DocumentType', header: 'Type' },
    { key: 'DocumentNumber', header: 'Number' },
    { key: 'IssueDate', header: 'Issue' },
    { key: 'ExpiryDate', header: 'Expiry' },
    { key: 'IssuingAuthority', header: 'Authority' },
    { key: 'FilePath', header: 'File / URL' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'DocumentType', label: 'Document type', type: 'text', required: true },
    { name: 'DocumentNumber', label: 'Document number', type: 'text' },
    { name: 'IssueDate', label: 'Issue date', type: 'date' },
    { name: 'ExpiryDate', label: 'Expiry date', type: 'date' },
    { name: 'IssuingAuthority', label: 'Issuing authority', type: 'text' },
    { name: 'FilePath', label: 'File path or URL', type: 'text' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

export const VEHICLE_INSURANCE = {
  title: 'Vehicle insurance',
  subtitle: 'Policies and premiums',
  gridKey: 'VehicleInsurancesInGrid',
  saveKey: 'SaveVehicleInsurance',
  deleteKey: 'DeleteVehicleInsurance',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'InsuranceCompany', header: 'Company' },
    { key: 'PolicyNumber', header: 'Policy #' },
    { key: 'PolicyType', header: 'Type' },
    { key: 'StartDate', header: 'Start' },
    { key: 'ExpiryDate', header: 'Expiry' },
    { key: 'PremiumAmount', header: 'Premium' },
    { key: 'CoverageAmount', header: 'Coverage' },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'InsuranceCompany', label: 'Insurance company', type: 'text', required: true },
    { name: 'PolicyNumber', label: 'Policy number', type: 'text', required: true },
    { name: 'PolicyType', label: 'Policy type', type: 'text' },
    { name: 'StartDate', label: 'Start date', type: 'date', required: true },
    { name: 'ExpiryDate', label: 'Expiry date', type: 'date', required: true },
    { name: 'PremiumAmount', label: 'Premium amount', type: 'number', step: '0.01', required: true },
    { name: 'CoverageAmount', label: 'Coverage amount', type: 'number', step: '0.01', nullable: true },
    { name: 'AgentName', label: 'Agent name', type: 'text' },
    { name: 'AgentContact', label: 'Agent contact', type: 'text' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};

export const VEHICLE_FINES = {
  title: 'Vehicle fine records',
  subtitle: 'Traffic fines and payments',
  gridKey: 'VehicleFineRecordsInGrid',
  saveKey: 'SaveVehicleFineRecord',
  deleteKey: 'DeleteVehicleFineRecord',
  columns: [
    { key: 'VehiclePlate', header: 'Vehicle' },
    { key: 'FineNumber', header: 'Fine #' },
    { key: 'ViolationType', header: 'Violation' },
    { key: 'Amount', header: 'Amount' },
    { key: 'FineDate', header: 'Date' },
    { key: 'Location', header: 'Location' },
    {
      key: 'IsPaid',
      header: 'Paid',
      render: (row) => (row.IsPaid ? 'Yes' : 'No'),
    },
    { key: 'Notes', header: 'Notes' },
  ],
  formFields: [
    { name: 'DriverId', label: 'Driver (employee id)', type: 'number', nullable: true },
    { name: 'FineNumber', label: 'Fine number', type: 'text', required: true },
    { name: 'ViolationType', label: 'Violation type', type: 'text', required: true },
    { name: 'Amount', label: 'Amount', type: 'number', step: '0.01', required: true },
    { name: 'FineDate', label: 'Fine date', type: 'date', required: true },
    { name: 'Location', label: 'Location', type: 'text' },
    { name: 'IsPaid', label: 'Paid', type: 'checkbox' },
    { name: 'PaymentDate', label: 'Payment date', type: 'date' },
    { name: 'Notes', label: 'Notes', type: 'textarea', rows: 3 },
  ],
};
